import {
  EVOLUTIONS,
  RARITY_WEIGHTS,
  SHOP_UPGRADES,
  SYNERGIES,
  UPGRADES,
  getCore,
  getUpgrade,
} from '../../data/content';
import { SeededRng } from './Rng';
import type {
  CoreStats,
  EvolutionDefinition,
  MetaModifiers,
  RunState,
  ShopOffer,
  ShopUpgradeId,
  SynergyDefinition,
  UpgradeDefinition,
  UpgradeOffer,
} from './types';

export function createCoreStats(meta: MetaModifiers): CoreStats {
  const definition = getCore(meta.selectedCoreId);
  const masteryMultiplier = 1 + meta.coreMasteryLevel * 0.025;
  const maxHealth =
    520 * (1 + meta.integrityLevel * 0.02) * definition.modifiers.maxHealth * masteryMultiplier;
  const maxBarrier =
    definition.ability.type === 'barrier'
      ? definition.ability.power * (1 + meta.coreMasteryLevel * 0.12)
      : 0;
  return {
    maxHealth,
    health: maxHealth,
    maxBarrier,
    barrier: maxBarrier,
    damageMultiplier:
      (1 + meta.powerLevel * 0.02) * definition.modifiers.damage * masteryMultiplier,
    attackSpeedMultiplier: definition.modifiers.attackSpeed,
    critChance: 0.05 + definition.modifiers.critChance,
    critMultiplier: 1.75,
    healthRegen: 0,
    armor: definition.modifiers.armor,
    pickupRadius: 90,
    xpMultiplier: 1 + meta.insightLevel * 0.02,
    cashMultiplier: 1,
    cooldownReduction: definition.modifiers.cooldownReduction,
    areaMultiplier: definition.modifiers.area,
  };
}

export function recalculateCoreStats(state: RunState, meta: MetaModifiers): void {
  const previousMaxHealth = state.core.maxHealth;
  const previousHealth = state.core.health;
  const previousMaxBarrier = state.core.maxBarrier;
  const previousBarrier = state.core.barrier;
  const next = createCoreStats(meta);

  for (const definition of UPGRADES) {
    const rank = state.upgradeRanks[definition.id] ?? 0;
    if (rank === 0) continue;
    for (const effect of definition.effects) {
      if (effect.type === 'core-multiplier') {
        const value = 1 + effect.perRank * rank;
        if (effect.stat === 'damage') next.damageMultiplier *= value;
        if (effect.stat === 'attackSpeed') next.attackSpeedMultiplier *= value;
        if (effect.stat === 'maxHealth') next.maxHealth *= value;
        if (effect.stat === 'xp') next.xpMultiplier *= value;
        if (effect.stat === 'cash') next.cashMultiplier *= value;
        if (effect.stat === 'area') next.areaMultiplier *= value;
        if (effect.stat === 'pickupRadius') next.pickupRadius *= value;
      }
      if (effect.type === 'core-add') {
        if (effect.stat === 'critChance') next.critChance += effect.perRank * rank;
        if (effect.stat === 'critMultiplier') next.critMultiplier += effect.perRank * rank;
        if (effect.stat === 'regen') next.healthRegen += effect.perRank * rank;
        if (effect.stat === 'armor') next.armor += effect.perRank * rank;
        if (effect.stat === 'cooldownReduction') next.cooldownReduction += effect.perRank * rank;
      }
    }
  }

  next.damageMultiplier *= 1 + (state.shopRanks.damage ?? 0) * 0.1;
  next.attackSpeedMultiplier *= 1 + (state.shopRanks['attack-speed'] ?? 0) * 0.08;
  next.maxHealth *= 1 + (state.shopRanks['max-health'] ?? 0) * 0.1;
  next.armor += (state.shopRanks.armor ?? 0) * 2;
  next.healthRegen += (state.shopRanks.regeneration ?? 0) * 0.7;
  next.critChance += (state.shopRanks.critical ?? 0) * 0.03;

  const gainedHealth = Math.max(0, next.maxHealth - previousMaxHealth);
  next.health = Math.min(next.maxHealth, previousHealth + gainedHealth);
  const gainedBarrier = Math.max(0, next.maxBarrier - previousMaxBarrier);
  next.barrier = Math.min(next.maxBarrier, previousBarrier + gainedBarrier);
  state.core = next;
}

export function generateUpgradeOffers(
  state: RunState,
  meta: MetaModifiers,
  rng: SeededRng,
): UpgradeOffer[] {
  const eligible = UPGRADES.filter((upgrade) => {
    const currentRank = state.upgradeRanks[upgrade.id] ?? 0;
    if (currentRank >= upgrade.maxRank) return false;
    if (upgrade.blueprintId !== null && !meta.unlockedBlueprints.includes(upgrade.blueprintId))
      return false;
    return requirementsMet(state, upgrade.requirements);
  });

  const selected: UpgradeDefinition[] = [];
  const remaining = [...eligible];
  while (selected.length < 3 && remaining.length > 0) {
    const weights = remaining.map((upgrade) => {
      let weight = RARITY_WEIGHTS[upgrade.rarity];
      if (upgrade.id === 'tesla_arc' && (state.upgradeRanks.tesla_arc ?? 0) === 0) weight *= 1.8;
      if (upgrade.id === 'cryo_emitter' && (state.upgradeRanks.cryo_emitter ?? 0) === 0)
        weight *= 1.8;
      return weight;
    });
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    let cursor = rng.next() * total;
    let chosenIndex = 0;
    for (let index = 0; index < remaining.length; index += 1) {
      cursor -= weights[index] ?? 0;
      if (cursor <= 0) {
        chosenIndex = index;
        break;
      }
    }
    selected.push(remaining.splice(chosenIndex, 1)[0]!);
  }

  return selected.map((upgrade) => {
    const currentRank = state.upgradeRanks[upgrade.id] ?? 0;
    return {
      id: upgrade.id,
      name: upgrade.name,
      description: upgrade.description,
      family: upgrade.family,
      rarity: upgrade.rarity,
      tier: upgrade.tier,
      maxRank: upgrade.maxRank,
      path:
        upgrade.requirements.length === 0
          ? 'ROOT SYSTEM'
          : `FROM ${upgrade.requirements.map((item) => getUpgrade(item.upgradeId).name).join(' + ')}`,
      currentRank,
      nextRank: currentRank + 1,
    };
  });
}

export function generateShopOffers(state: RunState, rng: SeededRng): ShopOffer[] {
  return rng
    .shuffle(SHOP_UPGRADES)
    .slice(0, 3)
    .map((definition) => {
      const rank = state.shopRanks[definition.id] ?? 0;
      const cost = Math.round(
        definition.baseCost * (1 + rank * 0.7 + Math.max(0, state.wave - 1) * 0.11),
      );
      return { ...definition, cost, affordable: state.cash >= cost };
    });
}

function requirementsMet(
  state: RunState,
  requirements: readonly { upgradeId: string; minRank: number }[],
): boolean {
  return requirements.every(
    (requirement) => (state.upgradeRanks[requirement.upgradeId] ?? 0) >= requirement.minRank,
  );
}

export function newlyUnlockedSynergies(state: RunState): SynergyDefinition[] {
  return SYNERGIES.filter(
    (synergy) =>
      !state.synergies.includes(synergy.id) && requirementsMet(state, synergy.requirements),
  );
}

export function eligibleEvolution(state: RunState): EvolutionDefinition | null {
  if (!state.evolutionTrigger || state.evolutionId !== null) return null;
  return (
    EVOLUTIONS.find(
      (evolution) =>
        state.synergies.includes(evolution.synergyId) &&
        requirementsMet(state, evolution.requirements),
    ) ?? null
  );
}

export function applyShopPurchase(state: RunState, shopId: ShopUpgradeId): boolean {
  const offer = state.shopOffers.find((candidate) => candidate.id === shopId);
  if (!offer || state.cash < offer.cost) return false;
  state.cash -= offer.cost;
  state.shopRanks[shopId] = (state.shopRanks[shopId] ?? 0) + 1;
  return true;
}

export function xpRequiredForLevel(level: number): number {
  return Math.round(12 + level * 5 + Math.pow(level, 1.35) * 1.8);
}

export function mitigateDamage(rawDamage: number, armor: number): number {
  return Math.max(0, rawDamage * (100 / (100 + Math.max(0, armor) * 5)));
}
